import { useGlobalData } from "@/app/global_data"
import {
  getCalculationValuesForComparison,
  getValuesForTerm,
} from "@/app/metric_utils"
import { useTray } from "@/app/providers/tray_provider"
import { RawHTML } from "@/app/raw_html"
import { Entity, Filter, GroupMemberValues, Metric } from "@/app/types"
import { Formatter } from "@/app/types/formatters"
import {
  applyRankPropertyInMembers,
  getMemberCountText,
  sortMembersColumns,
} from "@/components/comparison_group_list/comparison_group_list.functions"
import { TableHeader, TableRow } from "@/components/comparison_group_list/table"
import { ComparisonGroupTableProps } from "@/components/comparison_group_list/types"
import { useRouter } from "next/router"
import { useEffect, useMemo, useState } from "react"

export function ComparisonGroupTable({
  comparison,
  members,
  metric,
  term,
  sortOrder,
  setSortOrder,
  isFund,
  showDelta,
}: ComparisonGroupTableProps) {
  const { push } = useRouter()
  const { showTray } = useTray()

  const globalData = useGlobalData()
  const { entity, entityValues, allValues, filter } = globalData.get()

  const ascendingMetrics = [Metric.ReturnPtile, Metric.ExpenseRatio]
  const isCustomListSelected = comparison.isCustomList()
  const isFiltered = filter.key !== Filter.NONE.key
  const comparisonGroupName =
    isCustomListSelected ? "Comps" : comparison.getGroupName(isFund)
  const calculationName = comparison
    .getComparisonCalculation()
    .getCalcInfo("name", comparison.getCalculation())

  // including entity inside members array
  const allMembers = useMemo(() => {
    const entityMemberValues: GroupMemberValues = {
      filterIncluded: true,
      fund: entity.isFund() ? entity.getFund() : undefined,
      firm: entity.isFirm() ? entity.getFirm() : undefined,
      ranking: {
        [term.key]: getValuesForTerm(entityValues!, term.key)!,
      },
    }

    return comparison.getGroup() === "all" ?
        members
      : [...members, entityMemberValues]
  }, [members, term, entity])

  const [sortedMembers, setSortedMembers] =
    useState<GroupMemberValues[]>(allMembers)

  const formatters = Formatter.getFormatter(metric.name, "TABLE", true)

  const aggregatedComparisonValue = getCalculationValuesForComparison(
    entityValues,
    allValues,
    comparison,
    term,
    isFund,
    metric.name
  )

  function handleSortClick(column: string) {
    const isSameColumn = sortOrder.column === column
    const newDirection =
      isSameColumn && sortOrder.direction === "asc" ? "desc" : "asc"

    const sortedMemberList = sortMembersColumns(
      sortedMembers,
      newDirection === "asc",
      column,
      isFund,
      metric,
      term
    )

    setSortedMembers(sortedMemberList)
    setSortOrder({
      column,
      direction: newDirection,
      isSorted: true,
    })
  }

  // every time the metrics and term change
  // the members are sorted in descending order (Standard for initial state)
  // except for percentile metrics, which its supposed to be ascending order
  useEffect(() => {
    const isAscending = ascendingMetrics.includes(metric.name)
    const sortedMemberList = sortMembersColumns(
      allMembers,
      isAscending,
      "value",
      isFund,
      metric,
      term
    )

    const sortedMembersWithRank = applyRankPropertyInMembers(
      sortedMemberList,
      term.key,
      metric.name
    )

    setSortedMembers(sortedMembersWithRank)

    setSortOrder({
      column: "value",
      direction: isAscending ? "asc" : "desc",
      isSorted: false,
    })
  }, [metric, term, allMembers])

  return (
    <table
      className={`no-scrollbar flex w-full table-auto flex-col pl-2 pt-1 text-sm font-semibold text-main-500`}>
      <thead className="sticky top-0">
        <TableHeader
          sortOrder={sortOrder}
          onClickSorting={(columnName) => handleSortClick(columnName)}
        />

        {/* Aggregated Comparison Fixed Row */}
        <tr className="flex bg-main-100 px-3 text-secondary-500">
          <th className="overflow-y-scroll whitespace-nowrap py-1">-</th>
          <th className="flex-1 overflow-y-scroll whitespace-nowrap px-6 py-1 text-left">
            {calculationName + " " + comparisonGroupName}{" "}
            {getMemberCountText(isFiltered, sortedMembers)}
          </th>
          <th className="whitespace-nowrap py-1">
            <RawHTML>{formatters(aggregatedComparisonValue)}</RawHTML>
          </th>
        </tr>
      </thead>

      <tbody className="flex flex-col">
        {sortedMembers.map((memberValue, index) => {
          const memberEntity = new Entity(memberValue[entity.propertyName()])
          const memberId = memberEntity.getId()
          return (
            <TableRow
              key={index}
              member={memberValue}
              memberId={memberId}
              metric={metric}
              term={term}
              showDelta={showDelta}
              comparisonValue={aggregatedComparisonValue}
              onClick={() => {
                push(
                  `/overview/${entity.isFund() ? "fund" : "firm"}/${memberId}`
                )
                showTray(null)
              }}
            />
          )
        })}
      </tbody>
    </table>
  )
}
